import { useContext, useEffect, useMemo, useState } from 'react';
import { useGT } from 'gt-react';
import useCreateCoreEnum, {
  type CoreEnumResponse,
} from '@/api/core/core-enums/useCreateCoreEnum';
import useUpdateCoreEnum from '@/api/core/core-enums/useUpdateCoreEnum';
import { CoreEnumDTO } from '@/api/core/core-enums/useCoreEnums';
import { AuthContext } from '@/context/auth.context';
import { useViewMode } from '@/context/view-mode.context';
import { isAdmin as checkIsAdmin } from '@/utils/user';
import { toNamedEnumOptions } from './coreEnumOptions';

export interface EnumOption {
  id?: string;
  name: string;
  description: string;
  sortOrder: number;
}

export function useCoreEnumModal({
  open,
  onClose,
  enumToEdit,
  onSaved,
}: {
  open: boolean;
  onClose: () => void;
  enumToEdit?: CoreEnumDTO | null;
  onSaved?: (saved: CoreEnumResponse) => void;
}) {
  const { currentUser } = useContext(AuthContext);
  const { viewMode } = useViewMode();
  const isAdmin = checkIsAdmin(currentUser, viewMode);
  const gt = useGT();

  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isSystem, setIsSystem] = useState(false);
  const [areOptionsLocked, setAreOptionsLocked] = useState(false);
  const [options, setOptions] = useState<EnumOption[]>([]);

  const isEditMode = !!enumToEdit;

  const { mutate: createEnum, isPending: isCreating } = useCreateCoreEnum(
    (saved) => {
      onSaved?.(saved);
      onClose();
    },
  );
  const { mutate: updateEnum, isPending: isUpdating } = useUpdateCoreEnum(
    enumToEdit?.id || '',
    (saved) => {
      onSaved?.(saved);
      onClose();
    },
  );

  const isSubmitting = isCreating || isUpdating;

  useEffect(() => {
    if (!open) return;
    if (enumToEdit) {
      setName(enumToEdit.name);
      setDescription(enumToEdit.description);
      setIsSystem(enumToEdit.isSystem || false);
      setAreOptionsLocked(enumToEdit.areOptionsLocked);
      setOptions(
        enumToEdit.options?.map((opt) => ({
          id: opt.id,
          name: opt.name || '',
          description: opt.description || '',
          sortOrder: opt.sortOrder,
        })) ?? [],
      );
      return;
    }
    setName('');
    setDescription('');
    setIsSystem(false);
    setAreOptionsLocked(false);
    setOptions([
      { name: '', description: '', sortOrder: 0 },
      { name: '', description: '', sortOrder: 1 },
    ]);
  }, [open, enumToEdit]);

  function handleAddOption() {
    setOptions([
      ...options,
      {
        name: '',
        description: '',
        sortOrder: options.length,
      },
    ]);
  }

  function handleRemoveOption(index: number) {
    setOptions(options.filter((_, i) => i !== index));
  }

  function handleOptionChange(
    index: number,
    field: keyof EnumOption,
    value: string | number,
  ) {
    const newOptions = [...options];
    newOptions[index] = { ...newOptions[index], [field]: value };
    setOptions(newOptions);
  }

  function handleSubmit() {
    const shouldSubmitOptions = !isEditMode || !areOptionsLocked || isAdmin;
    const baseData = {
      name: name.trim(),
      description: description.trim(),
      ...(shouldSubmitOptions ? { options: toNamedEnumOptions(options) } : {}),
    };

    if (isEditMode) {
      updateEnum({
        ...baseData,
        ...(isAdmin && { isSystem }),
        ...(isAdmin && { areOptionsLocked }),
      });
      return;
    }
    createEnum({
      ...baseData,
      ...(isAdmin && { isSystem }),
      ...(isAdmin && { areOptionsLocked }),
    });
  }

  const canSubmit = useMemo(() => {
    if (isEditMode && enumToEdit?.isSystem) {
      return !isSubmitting;
    }
    const hasNamedOption = options.some((opt) => opt.name.trim().length > 0);
    return Boolean(
      name.trim() && description.trim() && hasNamedOption && !isSubmitting,
    );
  }, [
    name,
    description,
    options,
    isEditMode,
    enumToEdit?.isSystem,
    isSubmitting,
  ]);

  return {
    gt,
    isAdmin,
    isEditMode,
    isSystem,
    setIsSystem,
    areOptionsLocked,
    setAreOptionsLocked,
    name,
    setName,
    description,
    setDescription,
    options,
    isSubmitting,
    canSubmit,
    handleAddOption,
    handleRemoveOption,
    handleOptionChange,
    handleSubmit,
  };
}
